import type { MortalityLog } from '../../types/production'

// Re-export the shared D-3 pond → lot resolution so this module is the
// single import surface for MortalityCapture.tsx, mirroring
// feedCaptureValidation.ts.
export { resolvePondLots, type ResolvedPondLots } from './pondLotResolution'

// Mortality v0 PRD §11 AC #3/#4: the count is a whole number of individuals.
// `MortalityLogService.ts` bounds it `{ min: 0 }` — a `0` count is a valid,
// meaningful record ("checked, none dead"), so it is accepted; only empty,
// negative, non-integer and non-numeric input is rejected. There is no `0`
// default in the UI (PRD §11 AC #4) — an empty field is rejected here so
// the operator must enter a number explicitly.
export type MortalityCountValidation =
  | { ok: true; value: number }
  | { ok: false; reason: 'invalid' }

export const validateMortalityCountValue = (rawValue: string): MortalityCountValidation => {
  const trimmed = rawValue.trim()
  if (trimmed.length === 0) return { ok: false, reason: 'invalid' }

  const value = Number(trimmed)
  if (!Number.isFinite(value) || !Number.isInteger(value) || value < 0) {
    return { ok: false, reason: 'invalid' }
  }

  return { ok: true, value }
}

// Mortality v0 PRD §3 / §10 item 2: `mortalityCount` has no server-side
// maximum to mirror. v0 uses a fixed, non-blocking "confirm this is right"
// warning — the same warn-don't-block shape as Feed's
// LARGE_FEED_KG_WARNING_THRESHOLD. Per the Stage 1/2 O-1 escalation and the
// User decision this session, this threshold is a constant and is NEVER
// derived from lot population, stocked count, or any lot state — no
// mortality rate is computed to reach it (Charter O-1, O-3). Claude-proposed
// default per Charter U-2; re-tuned from real pilot data in Stage 5.
export const MORTALITY_COUNT_LARGE_WARNING_THRESHOLD = 1000

export const isUnusuallyLargeMortalityCount = (value: number): boolean =>
  value > MORTALITY_COUNT_LARGE_WARNING_THRESHOLD

// Mortality v0 PRD §3 / §5 / Stage 2 finding #3: the observed cause is an
// OPTIONAL selection from a fixed short list, persisted as a string code in
// `metadata.observedCause`. Never free text (strategy §6/§9). The list is
// hardcoded here — Crew Mode consumes it, it does not edit it (Charter O-4).
// Minimal v0 vocabulary; refined from pilot feedback in Stage 5 (PRD §10
// item 3).
export const MORTALITY_CAUSE_CODES = [
  'predation',
  'disease',
  'handling',
  'water_quality',
  'unknown',
  // Hatchery v0 (findings #84): egg and yolk-sac alevin pick-offs.
  'dead_egg',
  'not_hatched',
  'deformed',
  'fungus'
] as const

export type MortalityCauseCode = (typeof MORTALITY_CAUSE_CODES)[number]

/** The causes offered for a fish lot (grow-out). */
export const GROW_OUT_CAUSE_CODES: MortalityCauseCode[] = ['predation', 'disease', 'handling', 'water_quality', 'unknown']
/** The causes offered for an egg / yolk-sac alevin lot. */
export const HATCHERY_CAUSE_CODES: MortalityCauseCode[] = ['dead_egg', 'not_hatched', 'deformed', 'fungus', 'unknown']

/** Egg pick-offs run into the thousands: the "large number" check starts higher. */
export const HATCHERY_COUNT_LARGE_WARNING_THRESHOLD = 20000

export const isMortalityCauseCode = (value: unknown): value is MortalityCauseCode =>
  typeof value === 'string' && (MORTALITY_CAUSE_CODES as readonly string[]).includes(value)

// Mortality v0 PRD §3 / §10 P0 "Bloqueo de duplicados razonable": warn,
// never hard-block, on a same-lot entry within a short window. Same
// 15-minute default and warn-don't-block shape as WaterLog and Feed,
// scoped by `lotId` (Mortality is lot-scoped, D-1). Findings #158: with
// `causeCodes`, only a log with one of those causes counts — two causes in
// a row (No eclosionó, then Hongos) are two records, not a duplicate.
export const DUPLICATE_WARNING_WINDOW_MS = 15 * 60 * 1000

export const findLikelyDuplicateMortalityLog = (args: {
  lotId: string
  recordedAtIso: string
  existingLogs: MortalityLog[]
  causeCodes?: Array<string | null>
}): MortalityLog | null => {
  const candidateMs = Date.parse(args.recordedAtIso)
  if (!Number.isFinite(candidateMs)) return null

  for (const log of args.existingLogs) {
    if (log.lotId !== args.lotId) continue
    if (args.causeCodes) {
      const cause = (log.metadata as { observedCause?: unknown } | null | undefined)?.observedCause
      if (!args.causeCodes.includes(typeof cause === 'string' ? cause : null)) continue
    }

    const existingMs = Date.parse(log.recordedAt)
    if (!Number.isFinite(existingMs)) continue

    if (Math.abs(existingMs - candidateMs) <= DUPLICATE_WARNING_WINDOW_MS) {
      return log
    }
  }

  return null
}

export type MortalityCaptureCreatePayload = {
  farmId: string
  productionUnitId: string
  lotId: string
  lot?: string
  mortalityCount: number
  recordedAt: string
  capturedByActorId: string | undefined
  notes?: string
  metadata?: Record<string, unknown>
}

/**
 * Builds the create payload for a single Crew Mode Mortality capture. Pure
 * and independently testable — mirrors `buildFeedCaptureCreatePayload`'s
 * discipline:
 * - always stamps `capturedByActorId` from the active operator session
 *   (Charter D-4) rather than leaving it for the server's device-fallback;
 * - carries only facts — a count, and an optional observed-cause CODE
 *   (never free text, never a derived indicator). No mortality rate,
 *   cumulative mortality, survival %, or lot population appears here
 *   (Charter O-1, O-3);
 * - writes the cause to `metadata.observedCause` and never touches the
 *   reserved `metadata.i8c3GuardMode` key (`MortalityLogService.ts`);
 * - stamps `lot` (the lot's plain-text key) alongside the authoritative
 *   `lotId` — the admin overlay bridge (mortalityLogBridge.ts) keys its
 *   `(lot, day)` rollup on this string field, not `lotId`. Without it the
 *   count is stored correctly but never surfaces in Biomass/Database.
 */
export const buildMortalityCaptureCreatePayload = (args: {
  farmId: string
  productionUnitId: string
  lotId: string
  lot?: string
  mortalityCount: number
  recordedAtIso: string
  capturedByActorId: string | undefined
  causeCode: MortalityCauseCode | null
}): MortalityCaptureCreatePayload => {
  const payload: MortalityCaptureCreatePayload = {
    farmId: args.farmId,
    productionUnitId: args.productionUnitId,
    lotId: args.lotId,
    mortalityCount: args.mortalityCount,
    recordedAt: args.recordedAtIso,
    capturedByActorId: args.capturedByActorId
  }

  if (args.lot) {
    payload.lot = args.lot
  }

  if (args.causeCode) {
    payload.metadata = { observedCause: args.causeCode }
  }

  return payload
}

// Crew Mode Field Bundle v0 §3 item 8 — one visit to a tank.
//
// - 'deaths': one MortalityLog per cause row (a row may have no cause), all
//   sharing `metadata.visitId`;
// - 'none': "Sin mortalidad hoy" — one explicit zero (`noMortality: true`);
// - 'notChecked': "No se pudo revisar" — ASC 4.1.1.10.4 reason recorded
//   (`notChecked: true`, count 0 because the model requires one; readers treat
//   it as "no revisado", never as zero deaths).
// A past day ("Ayer" / "Hace 2 días") marks every row `lateEntry: true`.
export const NOT_CHECKED_REASON_CODES = ['weather', 'equipment', 'no_staff', 'other'] as const
export type NotCheckedReasonCode = (typeof NOT_CHECKED_REASON_CODES)[number]

export type MortalityVisit =
  | { kind: 'deaths'; rows: Array<{ causeCode: MortalityCauseCode | null; count: number }> }
  | { kind: 'none' }
  | { kind: 'notChecked'; reasonCode: NotCheckedReasonCode }

export const buildMortalityVisitPayloads = (args: {
  farmId: string
  productionUnitId: string
  lotId: string
  lot?: string
  recordedAtIso: string
  capturedByActorId: string | undefined
  visitId: string
  lateEntry: boolean
  note?: string
  visit: MortalityVisit
}): MortalityCaptureCreatePayload[] => {
  const note = args.note?.trim() || undefined
  const base = {
    farmId: args.farmId,
    productionUnitId: args.productionUnitId,
    lotId: args.lotId,
    ...(args.lot ? { lot: args.lot } : {}),
    recordedAt: args.recordedAtIso,
    capturedByActorId: args.capturedByActorId,
    ...(note ? { notes: note } : {})
  }
  const shared = {
    visitId: args.visitId,
    ...(args.lateEntry ? { lateEntry: true } : {})
  }

  if (args.visit.kind === 'none') {
    return [{ ...base, mortalityCount: 0, metadata: { ...shared, noMortality: true } }]
  }
  if (args.visit.kind === 'notChecked') {
    return [{ ...base, mortalityCount: 0, metadata: { ...shared, notChecked: true, notCheckedReason: args.visit.reasonCode } }]
  }
  return args.visit.rows.map((row) => ({
    ...base,
    mortalityCount: row.count,
    metadata: { ...shared, ...(row.causeCode ? { observedCause: row.causeCode } : {}) }
  }))
}
